import { useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnoseEzyVet, type Diagnostics } from "@/api/ezyvet";
import { errorKind } from "@/api/errors";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { formatDuration } from "@/utils/ezyvetModels";
import type { ApiError } from "@/types";

function scopesOf(d: Diagnostics): string[] | null {
  const scope = d.status.scope;
  return scope ? scope.split(/[\s,]+/).filter(Boolean) : null;
}

function latencyTone(ms: number) {
  return ms < 800 ? "synced" : ms < 2500 ? "discrepancy" : "unbilled";
}

/**
 * "Test connection": has VetGuard request a brand-new token from ezyVet with
 * the stored credentials, then shows the token state, granted scopes and
 * round-trip time (browser → VetGuard → ezyVet → back).
 */
export function ConnectionDiagnostics({ disabled }: { disabled?: boolean }) {
  const qc = useQueryClient();
  const test = useMutation<Diagnostics, ApiError>({
    mutationFn: diagnoseEzyVet,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["integrations", "ezyvet"] });
      qc.invalidateQueries({ queryKey: ["ezyvet", "telemetry"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const result = test.data;
  const scopes = result ? scopesOf(result) : null;
  const expires = result?.status.token_expires_at ? Date.parse(result.status.token_expires_at) : null;

  return (
    <div className="subsection">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Connection diagnostics</h3>
        <button type="button" className="btn btn-sm" disabled={disabled || test.isPending} onClick={() => test.mutate()}>
          {test.isPending ? "Testing…" : "Test connection"}
        </button>
      </div>

      <div aria-live="polite">
        {test.error && (
          <div className="callout error" style={{ marginTop: 12 }}>
            {errorKind(test.error) === "auth"
              ? `ezyVet rejected the stored credentials: ${test.error.message}`
              : errorKind(test.error) === "network"
                ? "Couldn't reach VetGuard. Check your connection and try again."
                : errorKind(test.error) === "rate_limited"
                  ? "ezyVet's rate limit is reached. Try again in a minute."
                  : test.error.message}
          </div>
        )}

        {result && !test.isPending && (
          <dl className="kv kv--compact">
            <dt>Token</dt>
            <dd>
              {result.status.status === "active" && expires ? (
                <StatusBadge tone="synced">Issued</StatusBadge>
              ) : (
                <StatusBadge tone="unbilled">Not issued</StatusBadge>
              )}
              {expires && <span className="muted"> · expires in {formatDuration((expires - Date.now()) / 1000)}</span>}
            </dd>
            <dt>Scopes</dt>
            <dd>
              {scopes ? (
                <span className="chip-list">
                  {scopes.map((s) => (
                    <span key={s} className="chip mono">
                      {s}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="muted">Default scopes for these credentials (ezyVet doesn&apos;t list them)</span>
              )}
            </dd>
            <dt>Latency</dt>
            <dd>
              <StatusBadge tone={latencyTone(result.latency_ms)}>{result.latency_ms} ms</StatusBadge>
              <span className="muted"> round trip, including the token request</span>
            </dd>
          </dl>
        )}
      </div>
    </div>
  );
}
